import React, { useEffect, useMemo, useState, useRef } from "react";
import { storage } from "../firebaseConfig";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { addProduct, updateProduct, getProduct } from "../services/productService";
import { listBrands } from "../services/brandService";
import { listCategories } from "../services/categoryService";
import { toast } from "react-toastify";

export default function ProductForm({ onSaved, editingId, focusKey }) {
  /* ---------------- helpers prix ---------------- */
  const toNumberPrice = (s) => {
    const n = Number(String(s || "").replace(/[^\d]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const fmtInt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n || 0)));

  /* ---------------- state ---------------- */
  const initialForm = () => ({
    title: "",
    description: "",
    price: 0, // ✅ number en FCFA (pas de centimes)
    stock: 0,
    categoryId: "",
    brandId: "",
    status: "active",
    variants: [],
    images: [],
    // ✅ champs prix texte
    priceText: "",
    priceTextPromo: "",
  });

  const [form, setForm] = useState(initialForm());
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [autoStock, setAutoStock] = useState(false);
  const titleRef = useRef(null);

  // ✅ focus quand on clique Éditer (ou quand editingId change)
  useEffect(() => {
    if (!editingId) return;
    const t = setTimeout(() => {
      titleRef.current?.focus();
      titleRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [editingId, focusKey]);
  /* --------------- bootstrap -------------- */
  useEffect(() => {
    (async () => {
      try {
        const [bRes, cRes] = await Promise.all([
          listBrands({ pageSize: 100 }),
          listCategories({ pageSize: 100 }),
        ]);
        setBrands(bRes.items || []);
        setCategories(cRes.items || []);
      } catch {
        toast.error("Impossible de charger marques/catégories");
      }
    })();
  }, []);

  // load product (edit)
  useEffect(() => {
    (async () => {
      if (!editingId) {
        setForm(initialForm());
        setFiles([]);
        setProgress(0);
        setAutoStock(false);
        return;
      }

      const p = await getProduct(editingId);
      if (!p) return;

      setForm({
        title: p.title || "",
        description: p.description || "",
        // ✅ on garde price number (utile pour compat)
        price: Number(p.price || 0),
        stock: Number(p.stock || 0),
        categoryId: p.categoryId || "",
        brandId: p.brandId || "",
        status: p.status || "active",
        variants: Array.isArray(p.variants) ? p.variants : [],
        images: Array.isArray(p.images) ? p.images : [],
        // ✅ on charge aussi les champs texte
        priceText: String(p.priceText || (p.price ? fmtInt(p.price) : "")).trim(),
        priceTextPromo: String(p.priceTextPromo || "").trim(),
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  /* --------------- helpers --------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ pour stock uniquement
    if (name === "stock") {
      setForm((f) => ({ ...f, stock: Math.max(0, Number(value || 0)) }));
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };

  // ✅ chaque fois que priceText / priceTextPromo change => recalcul price number
  useEffect(() => {
    const normal = toNumberPrice(form.priceText);
    const promo = toNumberPrice(form.priceTextPromo);
    const finalPrice = promo > 0 ? promo : normal;

    setForm((f) => ({ ...f, price: finalPrice }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.priceText, form.priceTextPromo]);

  /* -------- variantes -------- */
  const addVariant = () =>
    setForm((f) => ({
      ...f,
      variants: [...f.variants, { size: "", color: "", stock: 0 }],
    }));

  const updateVariant = (i, key, value) =>
    setForm((f) => {
      const variants = [...f.variants];
      variants[i] = {
        ...variants[i],
        [key]: key === "stock" ? Math.max(0, Number(value || 0)) : value,
      };
      const sum = variants.reduce((s, v) => s + (Number(v.stock) || 0), 0);
      return { ...f, variants, stock: autoStock ? sum : f.stock };
    });

  const removeVariant = (i) =>
    setForm((f) => {
      const variants = f.variants.filter((_, idx) => idx !== i);
      const sum = variants.reduce((s, v) => s + (Number(v.stock) || 0), 0);
      return { ...f, variants, stock: autoStock ? sum : f.stock };
    });

  const totalStockFromVariants = useMemo(
    () => (form.variants || []).reduce((s, v) => s + (Number(v.stock) || 0), 0),
    [form.variants]
  );

  useEffect(() => {
    if (!autoStock) return;
    setForm((f) => ({ ...f, stock: totalStockFromVariants }));
  }, [autoStock, totalStockFromVariants]);

  /* -------- images existantes -------- */
  const setPrimaryImage = (idx) =>
    setForm((f) => {
      const arr = [...(f.images || [])];
      if (idx <= 0) return f;
      const [img] = arr.splice(idx, 1);
      arr.unshift(img);
      return { ...f, images: arr };
    });

  const moveImage = (idx, dir) =>
    setForm((f) => {
      const arr = [...(f.images || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, images: arr };
    });

  const removeExistingImage = (idx) =>
    setForm((f) => ({
      ...f,
      images: (f.images || []).filter((_, i) => i !== idx),
    }));

  /* -------- nouveaux fichiers -------- */
  const previews = useMemo(() => files.map((f) => URL.createObjectURL(f)), [files]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);
  const removeNewFile = (i) => setFiles((arr) => arr.filter((_, idx) => idx !== i));

  /* -------- upload -------- */
  /* -------- upload (FIX no-loop-func) -------- */
const uploadAll = async () => {
  if (!files.length) return [];

  const urls = [];
  const totalBytes = files.reduce((s, f) => s + (f?.size || 0), 0) || 1;

  // on stocke l'avancement par fichier (bytesTransferred)
  const perFileBytes = new Array(files.length).fill(0);

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const r = ref(storage, `products/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(r, file);

    await new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (snap) => {
          // ✅ met à jour uniquement l’index i (pas de compteur global capturé)
          perFileBytes[i] = snap.bytesTransferred || 0;

          const uploadedTotal = perFileBytes.reduce((a, b) => a + b, 0);
          setProgress(Math.round((uploadedTotal / totalBytes) * 100));
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            urls.push(url);
            resolve();
          } catch (e) {
            reject(e);
          }
        }
      );
    });
  }

  setProgress(0);
  return urls;
};

  // const uploadAll = async () => {
  //   if (!files.length) return [];
  //   const totalBytes = files.reduce((s, f) => s + f.size, 0);
  //   let uploaded = 0;
  //   const urls = [];

  //   for (const file of files) {
  //     const r = ref(storage, `products/${Date.now()}-${file.name}`);
  //     const task = uploadBytesResumable(r, file);

  //     await new Promise((resolve, reject) => {
  //       task.on(
  //         "state_changed",
  //         (snap) => {
  //           const delta = snap.bytesTransferred - (task._last || 0);
  //           task._last = snap.bytesTransferred;
  //           uploaded += Math.max(0, delta);
  //           setProgress(Math.round((uploaded / totalBytes) * 100));
  //         },
  //         reject,
  //         async () => {
  //           const url = await getDownloadURL(task.snapshot.ref);
  //           urls.push(url);
  //           resolve();
  //         }
  //       );
  //     });
  //   }
  //   setProgress(0);
  //   return urls;
  // };

  /* -------- validations -------- */
  const errors = useMemo(() => {
    const e = {};

    if (!form.title?.trim()) e.title = "Titre requis";
    // if (!form.brandId) e.brandId = "Marque requise";
    if (!form.categoryId) e.categoryId = "Catégorie requise";

    const normal = toNumberPrice(form.priceText);
    const promo = toNumberPrice(form.priceTextPromo);

    if (!normal || normal <= 0) e.priceText = "Prix normal requis";
    if (promo && promo >= normal) e.priceTextPromo = "La promo doit être < au prix normal";

    if (!(Number.isFinite(form.stock) && form.stock >= 0)) e.stock = "Stock invalide";

    const seen = new Set();
    (form.variants || []).forEach((v, i) => {
      const key = `${(v.size || "").toLowerCase()}|${(v.color || "").toLowerCase()}`;
      if (seen.has(key)) e[`variant_${i}`] = "Doublon taille/couleur";
      seen.add(key);
    });

    return e;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  const pricePreview = useMemo(() => {
    const normal = toNumberPrice(form.priceText);
    const promo = toNumberPrice(form.priceTextPromo);
    const finalPrice = promo > 0 ? promo : normal;
    return `${fmtInt(finalPrice)} FCFA`;
  }, [form.priceText, form.priceTextPromo]);

  /* ---------------- submit ---------------- */
  const onSubmit = async (e) => {
    e.preventDefault();

    if (hasErrors) {
      toast.warn("Merci de corriger les erreurs du formulaire");
      return;
    }

    try {
      setSaving(true);

      const uploaded = await uploadAll();

      const normal = toNumberPrice(form.priceText);
      const promo = toNumberPrice(form.priceTextPromo);
      const finalPrice = promo > 0 ? promo : normal;

      const payload = {
        ...form,
        // ✅ champs stockés
        priceText: String(form.priceText || "").trim(),
        priceTextPromo: String(form.priceTextPromo || "").trim(),
        price: finalPrice, // number (FCFA)
        images: [...(form.images || []), ...uploaded],
        variants: (form.variants || []).map((v) => ({
          size: (v.size || "").trim(),
          color: (v.color || "").trim(),
          stock: Math.max(0, Number(v.stock || 0)),
        })),
      };

      if (editingId) {
        await updateProduct(editingId, payload);
        toast.success("Produit mis à jour");
      } else {
        await addProduct(payload);
        toast.success("Produit ajouté");
      }

      // reset
      setForm(initialForm());
      setFiles([]);
      setProgress(0);
      setAutoStock(false);

      onSaved?.();
    } catch (err) {
      toast.error(err?.message || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  /* ------------------ UI ------------------ */
  return (
    <form onSubmit={onSubmit} className="pf-sheet">
      <div className="pf-body">
        {/* Détails */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <h6 className="text-uppercase text-muted mb-3">Détails</h6>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Titre</label>
                <input
                  ref={titleRef}
                  name="title"
                  className={`form-control ${errors.title ? "is-invalid" : ""}`}
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Ex: T-shirt coton bio"
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>

              {/* <div className="col-md-3">
                <label className="form-label">Marque</label>
                <select
                  name="brandId"
                  className={`form-select ${errors.brandId ? "is-invalid" : ""}`}
                  value={form.brandId}
                  onChange={handleChange}
                >
                  <option value="">— Sélectionner —</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                {errors.brandId && <div className="invalid-feedback">{errors.brandId}</div>}
              </div> */}

              <div className="col-md-3">
                <label className="form-label">Catégorie</label>
                <select
                  name="categoryId"
                  className={`form-select ${errors.categoryId ? "is-invalid" : ""}`}
                  value={form.categoryId}
                  onChange={handleChange}
                >
                  <option value="">— Sélectionner —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.categoryId && <div className="invalid-feedback">{errors.categoryId}</div>}
              </div>

              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows={3}
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Matière, coupe, entretien…"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ✅ PRIX (nouveau) */}
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-body">
            <h6 className="text-uppercase text-muted mb-3">Prix</h6>

            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="form-label">Prix normal (affiché)</label>
                <input
                  className={`form-control ${errors.priceText ? "is-invalid" : ""}`}
                  value={form.priceText}
                  onChange={(e) => setForm((f) => ({ ...f, priceText: e.target.value }))}
                  placeholder="Ex: 14 500"
                  inputMode="numeric"
                />
                {errors.priceText && <div className="invalid-feedback">{errors.priceText}</div>}
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label">Prix promo (optionnel)</label>
                <input
                  className={`form-control ${errors.priceTextPromo ? "is-invalid" : ""}`}
                  value={form.priceTextPromo}
                  onChange={(e) => setForm((f) => ({ ...f, priceTextPromo: e.target.value }))}
                  placeholder="Ex: 11 600"
                  inputMode="numeric"
                />
                {errors.priceTextPromo && <div className="invalid-feedback">{errors.priceTextPromo}</div>}
              </div>

              <div className="col-12">
                <div className="small text-muted">
                  Aperçu prix utilisé (champ <b>price</b>) : <b style={{ color: "#ff9800" }}>{pricePreview}</b>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stock + statut */}
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-body">
            <h6 className="text-uppercase text-muted mb-3">Stock</h6>

            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label d-flex align-items-center gap-2">
                  Stock <span className="badge rounded-pill text-bg-light">auto</span>
                </label>
                <div className="input-group">
                  <input
                    type="number"
                    name="stock"
                    className={`form-control ${errors.stock ? "is-invalid" : ""}`}
                    value={autoStock ? totalStockFromVariants : form.stock}
                    onChange={handleChange}
                    min={0}
                    disabled={autoStock}
                  />
                  <span className="input-group-text">
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={autoStock}
                        onChange={(e) => setAutoStock(e.target.checked)}
                        title="Calculer depuis les variantes"
                      />
                    </div>
                  </span>
                </div>
                {autoStock && <div className="form-text">Somme variantes : {totalStockFromVariants}</div>}
                {errors.stock && <div className="invalid-feedback d-block">{errors.stock}</div>}
              </div>

              <div className="col-md-4">
                <label className="form-label">Statut</label>
                <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                  <option value="active">active</option>
                  <option value="draft">draft</option>
                  <option value="archived">archived</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Variantes */}
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-body">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="text-uppercase text-muted m-0">Variantes</h6>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addVariant}>
                + Ajouter
              </button>
            </div>

            {form.variants.length === 0 && (
              <div className="text-muted small">Aucune variante. Ajoute tailles/couleurs si nécessaire.</div>
            )}

            <div className="vstack gap-2">
              {form.variants.map((v, i) => (
                <div key={i} className="pf-variant">
                  <input
                    className={`form-control ${errors[`variant_${i}`] ? "is-invalid" : ""}`}
                    placeholder="Taille (ex: M)"
                    value={v.size}
                    onChange={(e) => updateVariant(i, "size", e.target.value)}
                  />
                  <input
                    className={`form-control ${errors[`variant_${i}`] ? "is-invalid" : ""}`}
                    placeholder="Couleur (ex: Bleu)"
                    value={v.color}
                    onChange={(e) => updateVariant(i, "color", e.target.value)}
                  />
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Stock"
                    value={v.stock}
                    min={0}
                    onChange={(e) => updateVariant(i, "stock", e.target.value)}
                  />
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeVariant(i)}>
                    ×
                  </button>
                  {errors[`variant_${i}`] && (
                    <div className="invalid-feedback d-block w-100">{errors[`variant_${i}`]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Images */}
        <div className="card shadow-sm border-0 mt-3">
          <div className="card-body">
            <h6 className="text-uppercase text-muted mb-3">Images</h6>

            <div className="mb-3">
              <input
                type="file"
                accept="image/*"
                multiple
                className="form-control"
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
              />
              {!!files.length && (
                <div className="form-text">
                  {files.length} fichier(s) sélectionné(s){progress > 0 ? ` — ${progress}%` : ""}
                </div>
              )}
              {progress > 0 && (
                <div className="progress mt-2" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                  <div className="progress-bar" style={{ width: `${progress}%` }} />
                </div>
              )}
            </div>

            {files.length > 0 && (
              <>
                <div className="small text-muted mb-2">Nouvelles images (non encore enregistrées)</div>
                <div className="pf-media-grid">
                  {previews.map((u, i) => (
                    <div key={i} className="pf-tile">
                      <img src={u} alt={`new-${i}`} />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger pf-tile-action"
                        onClick={() => removeNewFile(i)}
                        title="Retirer"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {form.images?.length > 0 && (
              <>
                <div className="d-flex align-items-center justify-content-between mt-3">
                  <div className="small text-muted">Images existantes</div>
                  <div className="small text-muted">★ Couverture · ↑↓ Réordonner</div>
                </div>

                <div className="pf-media-grid mt-2">
                  {form.images.map((url, idx) => (
                    <div key={idx} className="pf-tile">
                      <img src={url} alt={`img-${idx}`} />
                      <div className="pf-tile-toolbar">
                        <button
                          type="button"
                          className={`btn btn-sm ${idx === 0 ? "btn-warning" : "btn-outline-light"}`}
                          title="Définir comme couverture"
                          onClick={() => setPrimaryImage(idx)}
                        >
                          ★
                        </button>
                        <div className="btn-group">
                          <button type="button" className="btn btn-sm btn-outline-light" disabled={idx === 0} onClick={() => moveImage(idx, -1)} title="Monter">
                            ↑
                          </button>
                          <button type="button" className="btn btn-sm btn-outline-light" disabled={idx === form.images.length - 1} onClick={() => moveImage(idx, +1)} title="Descendre">
                            ↓
                          </button>
                        </div>
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeExistingImage(idx)} title="Supprimer">
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* barre d’action sticky */}
      <div className="pf-actions">
        <button className="btn btn-success btn-lg" disabled={saving || hasErrors}>
          {saving ? "Enregistrement..." : editingId ? "Mettre à jour" : "Ajouter le produit"}
        </button>
        {hasErrors && <div className="text-danger small">Des champs sont invalides. Merci de vérifier.</div>}
      </div>
    </form>
  );
}
